import debounce from '@/lib/debounce.js';
import getLookupMethod from '@/lib/getLookupMethod.js';
import { randomId } from '@shared/utils/randomId.js';
import { ensureFilterSetRowIds } from '@shared/workflow/ensureFilterSetRowIds.js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import FilterContainerButtons from './FilterContainerButtons.jsx';
import FilterItem from './FilterItem.jsx';
import argumentSelectors from '@/redux/selectors/argumentSelectors.js';

const updateFiltersImmutably = (filters, updateFn) => {
  const newFilters = typeof structuredClone !== 'undefined' ? structuredClone(filters) : JSON.parse(JSON.stringify(filters));
  updateFn(newFilters);
  return newFilters;
};

const DEFAULT_FILTER_STATE = { filterSet: [], conjunction: 'and' };

const normalizeFilters = (filters) => {
  if (!filters || typeof filters !== 'object') return { ...DEFAULT_FILTER_STATE };
  return { ...filters, filterSet: filters.filterSet ?? [] };
};

const getCanUseManualLookupFieldEntry = ({ lookupSettings, usesProvidedFieldOptions }) => {
  return lookupSettings?.type === 'lookup' && !usesProvidedFieldOptions;
};

const FilterContainer = ({ dataSource, isTransformAction, lookupSettings, filters, fieldOptions, operatorOptions, isAdvanced = false, isIfElse = false, defaultOperator, customOperators = false, useDynamicFields = false, onChange, disabled = false }) => {
  const isNetsuite = useSelector((state) => argumentSelectors[dataSource].activeApp(state)?.slug === 'netsuite');
  const lookupMethod = useMemo(() => getLookupMethod(dataSource), [dataSource]);
  const { lookup, loading, reload } = lookupMethod({ id: 'filter', lookupSettings });
  const currentFiltersRef = useRef(normalizeFilters(filters));
  const [useManualLookupFieldEntry, setUseManualLookupFieldEntry] = useState(false);

  const usesProvidedFieldOptions = Array.isArray(fieldOptions);
  const finalFieldOptions = usesProvidedFieldOptions || isTransformAction ? fieldOptions : lookup;
  const finalLoading = usesProvidedFieldOptions ? false : loading;
  const finalReload = usesProvidedFieldOptions ? undefined : reload;

  const formattedFieldOptions = useMemo(() => {
    if (!isNetsuite) {
      return finalFieldOptions;
    }
    return finalFieldOptions?.map((option) => ({ ...option, tag: option.value }));
  }, [finalFieldOptions, isNetsuite]);

  const calculatedDefaultOperator = useMemo(() => {
    if (defaultOperator) {
      return defaultOperator;
    }
    if (operatorOptions && operatorOptions.length > 0) {
      return operatorOptions[0].value;
    }
    return 'eq';
  }, [defaultOperator, operatorOptions]);

  const getNewFilter = useCallback(() => ({ id: randomId(), field: '', operator: calculatedDefaultOperator, value: '' }), [calculatedDefaultOperator]);
  const getNewGroup = useCallback(() => ({ id: randomId(), type: 'nested', conjunction: 'and', filterSet: [getNewFilter()] }), [getNewFilter]);

  const debouncedOnChange = useMemo(() => debounce(onChange, 1000), [onChange]);
  const canUseManualLookupFieldEntry = getCanUseManualLookupFieldEntry({ lookupSettings, usesProvidedFieldOptions });
  const shouldUseDynamicFields = useDynamicFields || useManualLookupFieldEntry;
  const handleUseManualLookupFieldEntry = useCallback(() => setUseManualLookupFieldEntry(true), []);

  const handleAdd = useCallback(
    (id) => {
      if (disabled) {
        return;
      }
      const newFilter = getNewFilter();

      if (!id) {
        const newFilters = { ...currentFiltersRef.current, filterSet: [...currentFiltersRef.current.filterSet, newFilter] };
        currentFiltersRef.current = newFilters;
        onChange(newFilters);
        return;
      }

      const newFilters = updateFiltersImmutably(currentFiltersRef.current, (newFilters) => {
        const addSibling = (filterSet) => {
          for (let i = 0; i < filterSet.length; i++) {
            if (filterSet[i].id === id) {
              filterSet.splice(i + 1, 0, newFilter);
              return filterSet;
            }
            if (filterSet[i].type === 'nested' && filterSet[i].filterSet) {
              filterSet[i].filterSet = addSibling(filterSet[i].filterSet);
            }
          }
          return filterSet;
        };
        newFilters.filterSet = addSibling(newFilters.filterSet);
      });

      currentFiltersRef.current = newFilters;
      onChange(newFilters);
    },
    [onChange, getNewFilter, disabled]
  );

  const handleAddGroup = useCallback(
    (id) => {
      if (disabled) {
        return;
      }

      const newGroup = getNewGroup();
      if (!id) {
        const newFilters = { ...currentFiltersRef.current, filterSet: [...currentFiltersRef.current.filterSet, newGroup] };
        currentFiltersRef.current = newFilters;
        onChange(newFilters);
        return;
      }

      const newFilters = updateFiltersImmutably(currentFiltersRef.current, (newFilters) => {
        const addSibling = (filterSet) => {
          for (let i = 0; i < filterSet.length; i++) {
            if (filterSet[i].id === id) {
              filterSet.splice(i + 1, 0, newGroup);
              return filterSet;
            }
            if (filterSet[i].type === 'nested' && filterSet[i].filterSet) {
              filterSet[i].filterSet = addSibling(filterSet[i].filterSet);
            }
          }
          return filterSet;
        };
        newFilters.filterSet = addSibling(newFilters.filterSet);
      });

      currentFiltersRef.current = newFilters;
      onChange(newFilters);
    },
    [onChange, getNewGroup, disabled]
  );

  const handleDelete = useCallback(
    (id) => {
      if (disabled) {
        return;
      }
      const newFilters = updateFiltersImmutably(currentFiltersRef.current, (newFilters) => {
        const deleteFilter = (filterSet) => {
          // First filter out the deleted item
          const newFilterSet = filterSet.filter((filter) => filter.id !== id);

          // If filterSet is now empty, return empty array to trigger parent deletion
          if (newFilterSet.length === 0) {
            return [];
          }

          // Process any nested filterSets
          return newFilterSet
            .map((filter) => {
              if (filter.type === 'nested' && filter.filterSet) {
                const nestedResult = deleteFilter(filter.filterSet);
                // If nested filterSet is empty, filter out this nested group
                if (nestedResult.length === 0) {
                  return null;
                }
                return { ...filter, filterSet: nestedResult };
              }
              return filter;
            })
            .filter(Boolean); // Remove any null entries from empty nested groups
        };

        newFilters.filterSet = deleteFilter(newFilters.filterSet);
      });

      currentFiltersRef.current = newFilters;
      onChange(newFilters);
    },
    [onChange, disabled]
  );

  const handleFilterChange = useCallback(
    (id, key, value) => {
      if (disabled) {
        return;
      }
      const newFilters = updateFiltersImmutably(currentFiltersRef.current, (newFilters) => {
        const updateFilter = (filterSet) => {
          for (let i = 0; i < filterSet.length; i++) {
            if (filterSet[i].id === id) {
              filterSet[i][key] = value;
              return filterSet;
            }
            if (filterSet[i].type === 'nested' && filterSet[i].filterSet) {
              filterSet[i].filterSet = updateFilter(filterSet[i].filterSet);
            }
          }
          return filterSet;
        };
        newFilters.filterSet = updateFilter(newFilters.filterSet);
      });

      currentFiltersRef.current = newFilters;

      // Debounce only value changes (typing), make field/operator changes immediate
      if (key === 'value') {
        debouncedOnChange(newFilters);
      } else {
        onChange(newFilters);
      }
    },
    [onChange, debouncedOnChange, disabled]
  );

  const handleToggleDisabled = useCallback(
    (id) => {
      if (disabled) {
        return;
      }
      const newFilters = updateFiltersImmutably(currentFiltersRef.current, (newFilters) => {
        const toggleDisabled = (filterSet) => {
          for (let i = 0; i < filterSet.length; i++) {
            if (filterSet[i].id === id) {
              filterSet[i].disabled = !filterSet[i].disabled;
              return filterSet;
            }
            if (filterSet[i].type === 'nested' && filterSet[i].filterSet) {
              filterSet[i].filterSet = toggleDisabled(filterSet[i].filterSet);
            }
          }
          return filterSet;
        };
        newFilters.filterSet = toggleDisabled(newFilters.filterSet);
      });

      currentFiltersRef.current = newFilters;
      onChange(newFilters);
    },
    [onChange, disabled]
  );

  const handleConjunctionChange = useCallback(
    (id, value) => {
      if (disabled) {
        return;
      }
      const newFilters = updateFiltersImmutably(currentFiltersRef.current, (newFilters) => {
        const updateConjunction = (filterSet, parentObj) => {
          for (let i = 0; i < filterSet.length; i++) {
            if (filterSet[i].id === id) {
              parentObj.conjunction = value;
              return true;
            }
            if (filterSet[i].type === 'nested' && filterSet[i].filterSet) {
              const found = updateConjunction(filterSet[i].filterSet, filterSet[i]);
              if (found) return true;
            }
          }
          return false;
        };
        updateConjunction(newFilters.filterSet, newFilters);
      });

      currentFiltersRef.current = newFilters;
      onChange(newFilters);
    },
    [onChange, disabled]
  );

  // Memoize handlers for root level operations
  const handleRootAdd = useCallback(() => handleAdd(), [handleAdd]);
  const handleRootAddGroup = useCallback(() => handleAddGroup(), [handleAddGroup]);

  useEffect(() => {
    currentFiltersRef.current = normalizeFilters(filters);
  }, [filters]);

  // Every handler above targets rows by `id`, but rows written by producers
  // that predate the id contract (e.g. Cloudy step configuration) can lack
  // them — and with all ids undefined, every edit lands on the first row and
  // a single delete wipes the whole set. Repair missing ids as soon as the
  // editor opens and persist them through the normal onChange path.
  useEffect(() => {
    if (disabled) {
      return;
    }
    const withRowIds = ensureFilterSetRowIds(currentFiltersRef.current);
    if (withRowIds !== currentFiltersRef.current) {
      currentFiltersRef.current = withRowIds;
      onChange(withRowIds);
    }
  }, [filters, disabled, onChange]);

  return (
    <div className="comp-filter-container">
      {currentFiltersRef.current?.filterSet?.length > 0 && (
        <div className="comp-filters">
          <FilterItem //
            dataSource={dataSource}
            isTransformAction={isTransformAction}
            isIfElse={isIfElse}
            lookupSettings={lookupSettings}
            item={currentFiltersRef.current}
            fieldOptions={formattedFieldOptions}
            operatorOptions={operatorOptions}
            customOperators={customOperators}
            useDynamicFields={shouldUseDynamicFields}
            canUseManualFieldEntry={canUseManualLookupFieldEntry}
            onUseManualFieldEntry={handleUseManualLookupFieldEntry}
            onAdd={handleAdd}
            onAddGroup={handleAddGroup}
            onDelete={handleDelete}
            onConjunctionChange={handleConjunctionChange}
            onFilterChange={handleFilterChange}
            onToggleDisabled={handleToggleDisabled}
            isAdvanced={isAdvanced}
            loading={finalLoading}
            reload={finalReload}
          />
        </div>
      )}
      <FilterContainerButtons //
        disabled={disabled}
        isAdvanced={isAdvanced}
        onAdd={handleRootAdd}
        onAddGroup={handleRootAddGroup}
      />
    </div>
  );
};

export { getCanUseManualLookupFieldEntry, normalizeFilters };
export default FilterContainer;

/*
Filters structure:
{
  "filterSet": [
    {
      "id": "fltu4guiRjUn5QEba",
      "field": "fldnbI3mte3QME5WO",
      "operator": "=",
      "value": "foo",
      "disabled": false
    },
    {
      "id": "flteoe2PIfDkwO5nC",
      "field": "fldnbI3mte3QME5WO",
      "operator": "=",
      "value": "bar"
    },
    {
      "id": "fltkZiCiCWiMQoR7G",
      "type": "nested",
      "conjunction": "or",
      "disabled": false,
      "filterSet": [
        {
          "id": "flty08YVeXOwKVshG",
          "field": "fldnbI3mte3QME5WO",
          "operator": "=",
          "value": "subfoo",
          "disabled": false
        },
        {
          "id": "flt1f0PMTd2j5OHp0",
          "field": "fldnbI3mte3QME5WO",
          "operator": "=",
          "value": "subbar"
        }
      ]
    }
  ],
  "conjunction": "and"
}

- filterSet is an array of filters
- sibling of filterSet is conjunction (and, or)
- disabled is an optional boolean property that can be set on any filter or nested group
- when disabled is true, the filter/group should be excluded from filtering logic
*/
